import { FC, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Text,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useTheme,
  useDisclosure,
} from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useToastHook } from '../../../appToast';
import { useIgnorePrOrIssue } from '../api';
import Badge from '../Badge';
import IgnoreEntryModal from '../../../ignoreEntryModal';
import { hasGoals } from '../../utils/charts.utils';
import { NewDataGrid } from '../../../NewGridTable/NewDataGrid';
import { useMetricColumns } from './DefaultDetailsModal/useMetricColumns';
import { useDownloadCSV } from './DefaultDetailsModal/useDownloadCSV';
import { MetricModalHeader } from './DefaultDetailsModal/MetricModalHeader';
import { MetricModalLoading } from './DefaultDetailsModal/MetricModalLoading';
import { useGlobalState } from '../../../hooks/useGlobalState';
import {
  PR_CHART_KEYS,
  PR_AGEING_CHARTS,
  PR_HEALTH_INDEX_CHARTS,
  LOC_VS_PR_CYCLE_TIME_CHARTS,
  REVIEW_RESPONSIVENESS_INDEX_CHARTS,
  GOAL_METRIC_GIT_KEYS,
  COMMIT_CHART_KEYS,
  WORK_BREAKDOWN_CHARTS,
  ISSUE_CHART_KEYS,
  GOAL_METRIC_JIRA_KEYS,
  AuthorIdFilter,
  TeamFilter,
} from '../../../shared/utils';

interface DefaultDetailsModalProps {
  openDrawer: boolean;
  handleDrawerClose: () => void;
  defaultData: any;
  heading: string;
  subHeading: string;
  isFetching: boolean;
  handlePageClick: any;
  currentChartMeta: any;
  querykey?: string;
  startDate?: string;
  endDate?: string;
  filterBy: AuthorIdFilter | TeamFilter;
  payload: any;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  hideMoreDetails?: boolean;
}

export const DefaultDetailsModal: FC<DefaultDetailsModalProps> = ({
  openDrawer,
  handleDrawerClose,
  defaultData,
  heading,
  subHeading,
  isFetching,
  handlePageClick,
  currentChartMeta,
  querykey,
  startDate,
  endDate,
  filterBy,
  payload,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  hideMoreDetails,
}) => {
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const [newToast] = useToastHook();
  const { colors } = useTheme();
  const { mutate: ignorePrOrIssue, isLoading: ignorePrOrIssueLoading } =
    useIgnorePrOrIssue();
  const ignoreEntryModal = useDisclosure();
  const [hideEntryId, setHideEntryId] = useState('');
  const { globalState } = useGlobalState();
  const downloadAllowed = globalState?.dto?.downloadAllowed;

  const { downloadCSV, pending } = useDownloadCSV({
    currentChartMeta,
    startDate,
    endDate,
    filterBy,
  });

  const handlePrIgnoreOrIssue = (id: string) => {
    ignorePrOrIssue(
      {
        id,
        type: [
          ...PR_CHART_KEYS,
          ...PR_AGEING_CHARTS,
          ...PR_HEALTH_INDEX_CHARTS,
          ...LOC_VS_PR_CYCLE_TIME_CHARTS,
          ...REVIEW_RESPONSIVENESS_INDEX_CHARTS,
          ...GOAL_METRIC_GIT_KEYS,
          ...COMMIT_CHART_KEYS,
          ...WORK_BREAKDOWN_CHARTS,
        ].includes(currentChartMeta?.chartKey)
          ? 'ignoreGit'
          : [...ISSUE_CHART_KEYS, ...GOAL_METRIC_JIRA_KEYS].includes(
              currentChartMeta?.chartKey
            )
          ? 'ignoreIssue'
          : '',
      },
      {
        onSuccess: () => {
          newToast({
            message: 'The entry you ignored has been successfully processed.',
            status: 'success',
          });
          queryClient.invalidateQueries([querykey]);
          queryClient.invalidateQueries([currentChartMeta.chartKey]);
        },
        onError: () => {
          newToast({
            message:
              'There was an error processing your request. Please try again.',
            status: 'error',
          });
        },
      }
    );
  };

  const handleIgnoreClick = (id: string) => {
    setHideEntryId(id);
    ignoreEntryModal.onOpen();
  };

  const columns = useMetricColumns({
    defaultData,
    heading,
    currentChartMeta,
    onIgnoreClick: handleIgnoreClick,
    ignoreLoading: ignorePrOrIssueLoading,
    colors,
  });

  const subHeader = useMemo(
    () => (
      <>
        <Text
          fontFamily="heading"
          fontSize="sm"
          color={'text.secondary'}
          fontWeight={'medium'}
        >
          {subHeading}
        </Text>

        {defaultData && defaultData.data && defaultData?.data[0]?.readings && (
          <Flex mt={2}>
            {defaultData?.data[0]?.readings?.map((item: any) => (
              <Badge
                key={item.name}
                dotColor={item.color}
                text={
                  item.name &&
                  item.name.charAt(0).toUpperCase() + item.name.slice(1)
                }
              />
            ))}
          </Flex>
        )}
      </>
    ),
    [subHeading, defaultData]
  );

  return (
    <Modal
      isOpen={openDrawer}
      onClose={handleDrawerClose}
      size="6xl"
      isCentered
      scrollBehavior="inside"
      autoFocus={false}
    >
      <ModalOverlay />
      <ModalContent maxW="90vw">
        <ModalHeader pb={2}>
          <MetricModalHeader
            heading={heading}
            subHeader={subHeader}
            currentChartMeta={currentChartMeta}
            filterBy={filterBy}
            payload={payload}
            startDate={startDate}
            endDate={endDate}
            hideMoreDetails={hideMoreDetails}
            isFetching={isFetching}
            downloadCSV={downloadCSV}
            downloadPending={pending}
            downloadAllowed={downloadAllowed}
            pathname={pathname}
            hasGoals={hasGoals}
          />
        </ModalHeader>
        <ModalCloseButton mt={2} />
        <ModalBody pb={6}>
          <Stack spacing={4}>
            {isFetching && !defaultData?.data ? (
              <MetricModalLoading />
            ) : defaultData?.data && defaultData?.data.length > 0 ? (
              <Box
                borderWidth="1px"
                borderRadius="md"
                overflow="hidden"
                w="100%"
              >
                <NewDataGrid
                  data={defaultData.data}
                  columns={columns}
                  showLoader={false}
                  skeletonLoader={false}
                  sticky="firstCol"
                  useMaxH
                  showGridLines
                  infiniteScroll
                  hasNextPage={hasNextPage}
                  fetchNextPage={fetchNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                />
              </Box>
            ) : (
              <Box
                borderWidth="1px"
                borderRadius="md"
                overflow="hidden"
                w="100%"
                minH="200px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text
                  fontFamily="heading"
                  fontSize="sm"
                  color="text.secondary"
                  fontWeight="medium"
                >
                  No data found
                </Text>
              </Box>
            )}

            {ignoreEntryModal.isOpen && (
              <IgnoreEntryModal
                {...ignoreEntryModal}
                id={hideEntryId}
                handleIgnore={handlePrIgnoreOrIssue}
              />
            )}
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default DefaultDetailsModal;
